import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useTransform, type MotionValue } from 'framer-motion'
import { cn } from '@/lib/utils'

/**
 * Odometer number: each digit column rolls to the new value in the direction
 * the whole number moved. Ported from uiarc.dev "Animated counter" onto
 * framer-motion; type size, weight and color come from the parent, so it drops
 * into any existing number span. Use for numbers that change while in view
 * (live counts), not for every cell of a table.
 */

type Part = { key: string; digit: number } | { key: string; text: string }

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
const ROLL = { type: 'spring', duration: 0.6, bounce: 0 } as const
const MORPH = { type: 'spring', duration: 0.4, bounce: 0 } as const
const PRESENCE = { initial: { width: 0, opacity: 0 }, animate: { width: 'auto', opacity: 1 }, exit: { width: 0, opacity: 0 } }

/** Columns keyed by place value, so 999 → 1,000 keeps the ones column the ones column. */
function partsFor(value: number, locale: string): Part[] {
  const parts = new Intl.NumberFormat(locale, { maximumFractionDigits: 0, numberingSystem: 'latn' }).formatToParts(value)
  let place = parts.reduce((n, p) => n + (p.type === 'integer' ? p.value.length : 0), 0)
  return parts.flatMap((part, index): Part[] => {
    if (part.type === 'integer') return [...part.value].map((ch) => ({ key: `i${--place}`, digit: Number(ch) }))
    return [{ key: part.type === 'group' ? `g${place}` : `${part.type}${index}`, text: part.value }]
  })
}

/** One digit on the wheel; its offset from the wheel position sets where it sits and how visible it is. */
function Glyph({ position, digit }: { position: MotionValue<number>; digit: number }) {
  const offset = useTransform(position, (p) => ((((digit - p) % 10) + 15) % 10) - 5)
  const y = useTransform(offset, (o) => `${o}em`)
  const opacity = useTransform(offset, (o) => Math.max(0, 1 - Math.abs(o)))
  const visibility = useTransform(offset, (o) => (Math.abs(o) >= 1 ? 'hidden' : 'visible'))
  return (
    <motion.span className="absolute inset-x-0 inset-y-[0.16em] text-center" style={{ y, opacity, visibility }}>
      {digit}
    </motion.span>
  )
}

function Column({ digit, direction, reduceMotion }: { digit: number; direction: number; reduceMotion: boolean }) {
  const position = useMotionValue(digit)
  const wheel = useRef({ digit, target: digit })
  useEffect(() => {
    const state = wheel.current
    if (state.digit === digit) return
    state.target += direction < 0 ? -((state.digit - digit + 10) % 10) : (digit - state.digit + 10) % 10
    state.digit = digit
    if (reduceMotion) position.jump(state.target)
    else animate(position, state.target, ROLL)
  }, [digit, direction, position, reduceMotion])
  return (
    <motion.span
      {...PRESENCE}
      transition={reduceMotion ? { duration: 0 } : MORPH}
      className="relative -my-[0.16em] inline-block overflow-hidden py-[0.16em] [mask-image:linear-gradient(to_bottom,transparent,#000_0.24em,#000_calc(100%-0.24em),transparent)]"
    >
      <span className="invisible">0</span>
      {DIGITS.map((d) => (
        <Glyph key={d} position={position} digit={d} />
      ))}
    </motion.span>
  )
}

export function AnimatedCounter({ value, locale = 'en-US', className }: { value: number; locale?: string; className?: string }) {
  const reduceMotion = !!useReducedMotion()
  const [previous, setPrevious] = useState(value)
  const [direction, setDirection] = useState(1)
  if (value !== previous) {
    setPrevious(value)
    setDirection(value > previous ? 1 : -1)
  }
  const parts = partsFor(value, locale)
  return (
    <span className={cn('relative inline-flex tabular-nums', className)}>
      <span className="sr-only">{parts.map((p) => ('text' in p ? p.text : p.digit)).join('')}</span>
      <span className="inline-flex items-start whitespace-nowrap select-none" aria-hidden>
        <AnimatePresence initial={false}>
          {parts.map((part) =>
            'digit' in part ? (
              <Column key={part.key} digit={part.digit} direction={direction} reduceMotion={reduceMotion} />
            ) : (
              <motion.span key={part.key} {...PRESENCE} transition={reduceMotion ? { duration: 0 } : MORPH} className="inline-block overflow-x-clip">
                {part.text}
              </motion.span>
            ),
          )}
        </AnimatePresence>
      </span>
    </span>
  )
}
